import {
  NavigationDashboardTabItemDto,
  NavigationDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  QueryClient,
  QueryFunction,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';

import { extractErrorMessage } from '../../utils/errorMessage';
import { prependRoleId } from '../../utils/prependRoleId';
import { HttpError } from '../errors';
import { portalGatewayClient } from '../portalGatewayClient';

export const fetchNavigation: QueryFunction<NavigationDto> = async () => {
  const res = await portalGatewayClient.navigation.getNavigation();
  if (res.ok) {
    return res.data.data;
  }
  throw new HttpError(
    extractErrorMessage(res.error, 'Fetching navigation failed.'),
    res
  );
};

const queryOptions = {
  gcTime: Infinity,
  queryFn: fetchNavigation,
  retry: false,
};

export function getNavigationQueryKey() {
  return prependRoleId(['navigation']);
}

export function invalidateNavigationQuery(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    queryKey: getNavigationQueryKey(),
  });
}

export function patchDashboardNavItems(
  queryClient: QueryClient,
  sourcePageId: string,
  items: NavigationDashboardTabItemDto[]
) {
  queryClient.setQueryData<NavigationDto>(
    getNavigationQueryKey(),
    (current) => {
      if (current == null) {
        return current;
      }

      return {
        ...current,
        dashboardNavItems: {
          ...current.dashboardNavItems,
          [sourcePageId]: items,
        },
      };
    }
  );
}

export function getCachedDashboardNavItems(
  queryClient: QueryClient,
  sourcePageId: string
): NavigationDashboardTabItemDto[] {
  const navigation = queryClient.getQueryData<NavigationDto>(
    getNavigationQueryKey()
  );
  return navigation?.dashboardNavItems?.[sourcePageId] ?? [];
}

function simpleSlugFromName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '');
  return slug.length > 0 ? slug : 'dashboard';
}

/** Tab slug unique among pinned tabs (does not mirror full gateway page-dashboard slug rules). */
export function uniqueTabSlug(
  name: string,
  usedSlugs: ReadonlySet<string>
): string {
  const base = simpleSlugFromName(name);
  let candidate = base;
  let counter = 2;
  while (usedSlugs.has(candidate)) {
    candidate = `${base}-${counter}`;
    counter += 1;
  }
  return candidate;
}

export type DashboardNavCacheChange =
  | {
      op: 'add';
      instanceId: string;
      dashboardId: string;
      order: number;
      label: string;
    }
  | { op: 'remove'; dashboardId: string }
  | { op: 'replace'; items: NavigationDashboardTabItemDto[] };

/**
 * Updates only the cached dashboardNavItems for a source page after create,
 * delete, or All Dashboards modal save — without refetching /navigation.
 */
export function applyDashboardNavCacheChange(
  queryClient: QueryClient,
  sourcePageId: string,
  change: DashboardNavCacheChange
): NavigationDashboardTabItemDto[] {
  const current = getCachedDashboardNavItems(queryClient, sourcePageId);

  let next: NavigationDashboardTabItemDto[];
  if (change.op === 'add') {
    const usedSlugs = new Set(current.map((item) => item.slug));
    const slug = uniqueTabSlug(change.label, usedSlugs);
    const tab: NavigationDashboardTabItemDto = {
      type: 'navigation-dashboard-tab-item',
      instanceId: change.instanceId,
      dashboardId: change.dashboardId,
      slug,
      label: change.label,
      order: change.order,
    };
    next = [...current, tab].sort((a, b) => a.order - b.order);
  } else if (change.op === 'remove') {
    next = current.filter((item) => item.dashboardId !== change.dashboardId);
  } else {
    next = change.items;
  }

  patchDashboardNavItems(queryClient, sourcePageId, next);
  return next;
}

export function buildDashboardNavItemsAfterModalSave({
  orderedDashboardIds,
  instanceByDashboardId,
  existingNavItems,
  labelByDashboardId,
}: {
  orderedDashboardIds: readonly string[];
  instanceByDashboardId: ReadonlyMap<
    string,
    { id: string; dashboardId: string }
  >;
  existingNavItems: readonly NavigationDashboardTabItemDto[];
  labelByDashboardId: ReadonlyMap<string, string>;
}): NavigationDashboardTabItemDto[] {
  const existingByDashboardId = new Map(
    existingNavItems.map((item) => [item.dashboardId, item])
  );
  const remainingIds = new Set(orderedDashboardIds);
  const usedSlugs = new Set(
    existingNavItems
      .filter((item) => remainingIds.has(item.dashboardId))
      .map((item) => item.slug)
  );
  const items: NavigationDashboardTabItemDto[] = [];

  for (const [order, dashboardId] of orderedDashboardIds.entries()) {
    const instance = instanceByDashboardId.get(dashboardId);
    if (instance == null) {
      continue;
    }
    const existing = existingByDashboardId.get(dashboardId);
    const label =
      existing?.label ?? labelByDashboardId.get(dashboardId) ?? dashboardId;
    const slug = existing?.slug ?? uniqueTabSlug(label, usedSlugs);
    if (existing == null) {
      usedSlugs.add(slug);
    }
    items.push({
      type: 'navigation-dashboard-tab-item',
      instanceId: instance.id,
      dashboardId,
      slug,
      label,
      order,
    });
  }

  return items;
}

export function useNavigationSuspenseQuery() {
  return useSuspenseQuery({
    ...queryOptions,
    queryKey: getNavigationQueryKey(),
  });
}

export function useNavigationQuery() {
  return useQuery({
    ...queryOptions,
    queryKey: getNavigationQueryKey(),
  });
}
